import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toString } from 'lodash';
import * as Sentry from '@sentry/react';

import { UserInformation } from '../../user_management/types/IUserManagement';

import { localStorageKeys } from '@/utils/tokens';
import type { PermissionsLoadState } from '@/utils/forbiddenCause';
import { IWorkItemActiveColumns } from '@/apps/dispatcher/types/IWorkItem';

export type TempCollapsed = {
  navCollapsed: boolean;
  filtersCollapsed: boolean;
};

interface AppSettingsState {
  userInformation: UserInformation;
  /** Outcome of the boot-time permissions fetch — lets the app distinguish
   * "not loaded / failed" from "loaded but denied" (CBP-1097). */
  permissionsLoadState: PermissionsLoadState;
  currentTheme: 'light' | 'dark';
  signalRConnectionStatus:
    | 'connected'
    | 'disconnected'
    | 'reconnecting'
    | 'reconnected';
  isCompact: boolean;
  filtersCollapsed: boolean;
  navCollapsed: boolean;
  tempNavCollapsed: boolean;
  tempFiltersCollapsed: boolean;
}

export const initialCollapsed = () => {
  if (localStorage.getItem(localStorageKeys.navCollapsed) === null) {
    return false;
  } else {
    return localStorage.getItem(localStorageKeys.navCollapsed) === 'true'
      ? true
      : false;
  }
};

export const initialFiltersCollapsed = () => {
  if (localStorage.getItem(localStorageKeys.filtersCollapsed) === null) {
    return true;
  } else {
    return localStorage.getItem(localStorageKeys.filtersCollapsed) === 'true'
      ? true
      : false;
  }
};

export const initialColumnSettings = {
  type: true,
  contractId: true,
  depotName: true,
  lineNumber: true,
  departure: true,
  arrival: true,
  startingStopName: true,
  operatorExempt: false,
} satisfies Required<IWorkItemActiveColumns>;

const initialState: AppSettingsState = {
  userInformation: {
    ...({} as UserInformation),
    settings: {
      dispatcherSettings: {
        columnSettings: initialColumnSettings,
      },
    },
  },
  permissionsLoadState: 'idle',
  currentTheme:
    (localStorage.getItem(localStorageKeys.theme) as 'light' | 'dark') ||
    'light',
  signalRConnectionStatus: 'connected',
  isCompact: false,
  filtersCollapsed: initialFiltersCollapsed(),
  navCollapsed: initialCollapsed(),
  tempFiltersCollapsed: initialFiltersCollapsed(),
  tempNavCollapsed: initialCollapsed(),
};

if (typeof document !== 'undefined') {
  document.documentElement.setAttribute(
    'data-theme',
    initialState.currentTheme
  );
}

const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    setSignalRConnectionStatus(
      state,
      action: PayloadAction<
        'connected' | 'disconnected' | 'reconnecting' | 'reconnected'
      >
    ) {
      state.signalRConnectionStatus = action.payload;
    },
    setUserInformation(state, action: PayloadAction<UserInformation>) {
      state.userInformation = {
        ...state.userInformation,
        ...action.payload,
      };
    },
    updateUserInformation(
      state,
      action: PayloadAction<
        Partial<Omit<UserInformation, 'id' | 'name' | 'email'>>
      >
    ) {
      if (action.payload.permissions) {
        Sentry.setUser(action.payload.permissions);

        state.userInformation = {
          ...state.userInformation,
          permissions: action.payload.permissions,
        };
        state.permissionsLoadState = 'loaded';
      }
      if (action.payload.settings) {
        state.userInformation = {
          ...state.userInformation,
          settings: action.payload.settings,
        };
      }
      if (action.payload.displayName) {
        state.userInformation = {
          ...state.userInformation,
          displayName: action.payload.displayName,
        };
      }
    },
    setPermissionsLoadState(
      state,
      action: PayloadAction<PermissionsLoadState>
    ) {
      state.permissionsLoadState = action.payload;
    },
    setTheme(state, action: PayloadAction<'light' | 'dark'>) {
      state.currentTheme = action.payload;
      localStorage.setItem(localStorageKeys.theme, action.payload);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', action.payload);
      }
    },
    setIsCompact(state, action: PayloadAction<boolean>) {
      state.isCompact = action.payload;
    },
    setActiveWorkItemColumns(
      state,
      action: PayloadAction<IWorkItemActiveColumns>
    ) {
      if (!state.userInformation.settings) {
        state.userInformation.settings = {
          dispatcherSettings: {
            columnSettings: action.payload,
          },
        };
      } else if (!state.userInformation.settings.dispatcherSettings) {
        state.userInformation.settings.dispatcherSettings = {
          columnSettings: action.payload,
        };
      } else {
        state.userInformation.settings.dispatcherSettings.columnSettings =
          action.payload;
      }
    },
    setFiltersCollapsed(state, action: PayloadAction<boolean>) {
      state.filtersCollapsed = action.payload;
      localStorage.setItem(
        localStorageKeys.filtersCollapsed,
        toString(action.payload)
      );
    },
    setNavCollapsed(state, action: PayloadAction<boolean>) {
      state.navCollapsed = action.payload;
      localStorage.setItem(
        localStorageKeys.navCollapsed,
        toString(action.payload)
      );
    },
    setTempNavCollapsed(state, action: PayloadAction<boolean>) {
      state.tempNavCollapsed = action.payload;
    },
    setTempFiltersCollapsed(state, action: PayloadAction<boolean>) {
      state.tempFiltersCollapsed = action.payload;
    },
  },
});

export const {
  setSignalRConnectionStatus,
  setUserInformation,
  updateUserInformation,
  setPermissionsLoadState,
  setTheme,
  setIsCompact,
  setActiveWorkItemColumns,
  setFiltersCollapsed,
  setNavCollapsed,
  setTempFiltersCollapsed,
  setTempNavCollapsed,
} = appSettingsSlice.actions;
export default appSettingsSlice.reducer;
